import Toast from "@/components/Toast";
import "@/styles/main.scss";
import ProgressBar from "@badrap/bar-of-progress";
import { SessionProvider } from "next-auth/react";
import Head from "next/head";
import Router, { useRouter } from "next/router";
import { GoogleAnalytics } from "nextjs-google-analytics-gtm";
import React from "react";
import { Toaster } from "react-hot-toast";
import type { NextPage } from "next";
import type { AppProps } from "next/app";
import type { ReactElement, ReactNode } from "react";

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
  skipParentLayout?: boolean;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const allowedPaths = ["bootcamps", "onboarding", "premium-notification"];

const progress = new ProgressBar({
  size: 2,
  color: "#2363EB",
  className: "bar-of-progress",
  delay: 100,
});

if (typeof window !== "undefined") {
  progress.start();
  progress.finish();
}

Router.events.on("routeChangeStart", progress.start);
Router.events.on("routeChangeComplete", () => {
  progress.finish();
  window.scrollTo(0, 0);
});
Router.events.on("routeChangeError", progress.finish);

export default function App({ Component, pageProps }: AppPropsWithLayout) {
  const router = useRouter();

  const [enableWebengage, setEnableWebengage] = React.useState(false);

  const skipParentLayout = Component.skipParentLayout === true;

  React.useEffect(() => {
    if (typeof window === "undefined" || skipParentLayout) {
      return;
    }
    const pathname = router.asPath;
    const allowedRegexRoutes = allowedPaths.map(
      (path) => new RegExp(`^/${path}(\\/.*)?$`),
    );
    const isAllowed = allowedRegexRoutes.some((regex) => regex.test(pathname));
    if (isAllowed) {
      setEnableWebengage(true);
    }
  }, [router.asPath, skipParentLayout]);

  // If page opts out of parent layout, render just the component
  if (skipParentLayout) {
    return (
      <>
        <Head>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1"
          ></meta>
        </Head>
        <Component {...pageProps} />
      </>
    );
  }

  return (
    <>
      <Head>
        <GoogleAnalytics />
        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-R0QPWVG6LX"
        ></script>
        <script
          dangerouslySetInnerHTML={{
            __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());

            gtag('config', 'G-R0QPWVG6LX');
          `,
          }}
        ></script>
        {enableWebengage && (
          <script
            type="text/javascript"
            id="_webengage_script_tag"
            dangerouslySetInnerHTML={{
              __html: `var webengage;!function(e,t,n,i,o){function s(e,t){e[t[t.length-1]]=function(){a.__queue.push([t.join("."),arguments])}}var r,g,a=e[n],c="init options track screen onReady".split(" "),l="feedback survey notification".split(" "),p="options render clear abort".split(" "),u="Open Close Submit Complete View Click".split(" "),f="identify login logout setAttribute".split(" ");if(!a||!a.__v){for(e[n]=a={__queue:[],is_spa:1,__v:"6.0",user:{}},r=0;r<c.length;r++)s(a,[c[r]]);for(r=0;r<l.length;r++){for(a[l[r]]={},g=0;g<p.length;g++)s(a[l[r]],[l[r],p[g]]);for(g=0;g<u.length;g++)s(a[l[r]],[l[r],"on"+u[g]])}for(r=0;r<f.length;r++)s(a.user,["user",f[r]]);setTimeout(function(){var e=t.createElement("script"),n=t.getElementById("_webengage_script_tag");e.type="text/javascript",e.async=!0,e.src=("https:"==t.location?.protocol?"https://widgets.in.webengage.com":"http://widgets.in.webengage.com")+"/js/webengage-min-v-6.0.js",n.parentNode.insertBefore(e,n)})}}(window,document,"webengage"),webengage.init('in~82617217');`,
            }}
          ></script>
        )}
        <script
          dangerouslySetInnerHTML={{
            __html: `
          (function(c,l,a,r,i,t,y){
              c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
              t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "lty1y3p17h");`,
          }}
        ></script>
        <script
          dangerouslySetInnerHTML={{
            __html: `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window,document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init','1352961549489601'); 
              fbq('init','728384890280305');
              fbq('init','756578293783132');

              fbq('track', 'PageView');`,
          }}
        />

        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-DE9JLYGSSZ"
        ></script>
        <script
          dangerouslySetInnerHTML={{
            __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());

            gtag('config', 'G-DE9JLYGSSZ');
            gtag('config', 'AW-17497221645');
          `,
          }}
        ></script>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1"
        ></meta>
        <title>Tradewise</title>
      </Head>
      <Toast />
      <SessionProvider>
        <Component {...pageProps} />
        <Toaster position="top-center" />
      </SessionProvider>
    </>
  );
}
